import Component from 'flarum/common/Component';
import app from 'flarum/forum/app';
import AskQuestionPanel from './AskQuestionPanel';

/**
 * AskFloatingButton — 全局悬浮提问答疑按钮
 *
 * 固定在右下角，显示当前页面上下文的提问总数角标。
 * 点击弹出 AskQuestionPanel。
 *
 * 自动检测当前页面上下文：
 *   - 讨论页（/d/:id）→ targetType=discussion
 *   - 文章页（/article/:id）→ targetType=article
 *   - 其他页面 → 不显示
 *
 * 页面切换时（SPA 导航）自动更新上下文和数量。
 */
export default class AskFloatingButton extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.panelOpen = false;
    this.questionCount = 0;
    this.countLoading = false;
    this._prevPath = '';
  }

  oncreate(vnode) {
    super.oncreate(vnode);
    this.fetchCount();

    // 监听 SPA 页面切换（popstate 和 mithril redraw 后）
    window.addEventListener('popstate', this._onPageChange);
    // 用 MutationObserver 检测 mithril 页面内容变化
    this._observer = new MutationObserver(() => {
      const newPath = window.location.pathname;
      if (newPath !== this._prevPath) {
        this._prevPath = newPath;
        this._onPageChange();
      }
    });
    this._observer.observe(document.querySelector('#app') || document.body, {
      childList: true,
      subtree: true,
    });
  }

  onremove(vnode) {
    super.onremove(vnode);
    window.removeEventListener('popstate', this._onPageChange);
    if (this._observer) this._observer.disconnect();
  }

  _onPageChange = () => {
    this.panelOpen = false;
    this.fetchCount();
    m.redraw();
  };

  /**
   * 获取当前页面上下文
   */
  getContext() {
    const path = window.location.pathname;

    // 讨论页 /d/:id-*
    const discMatch = path.match(/\/d\/(\d+)/);
    if (discMatch) {
      return { targetType: 'discussion', targetId: discMatch[1] };
    }

    // 文章页 /article/:id
    const articleMatch = path.match(/\/article\/(\d+)/);
    if (articleMatch) {
      return { targetType: 'article', targetId: articleMatch[1] };
    }

    return null;
  }

  /** 是否应该显示按钮 */
  get visible() {
    return this.getContext() !== null;
  }

  /** 当前上下文 */
  get currentContext() {
    return this.getContext();
  }

  api(path) {
    return app.forum.attribute('apiUrl') + path;
  }

  fetchCount() {
    const ctx = this.currentContext;
    if (!ctx) return;

    this.countLoading = true;
    m.redraw();

    app
      .request({
        url: this.api('/taro/ask-questions/context'),
        method: 'GET',
        params: {
          targetType: ctx.targetType,
          targetId: ctx.targetId,
        },
      })
      .then((data) => {
        this.countLoading = false;
        this.questionCount = (data.data || []).length;
        m.redraw();
      })
      .catch(() => {
        this.countLoading = false;
        m.redraw();
      });
  }

  togglePanel() {
    this.panelOpen = !this.panelOpen;
    m.redraw();
  }

  closePanel() {
    this.panelOpen = false;
    m.redraw();
  }

  view() {
    if (!this.visible) return null;

    const ctx = this.currentContext;

    return (
      <div className="AskFloatingButton">
        {/* 悬浮按钮 */}
        <button
          className="AskFloatingButton-btn"
          onclick={() => this.togglePanel()}
          title="提问答疑"
        >
          <i className="fas fa-question AskFloatingButton-icon"></i>
          <span className="AskFloatingButton-label">提问</span>
          {this.questionCount > 0 && (
            <span className="AskFloatingButton-badge">{this.questionCount}</span>
          )}
        </button>

        {/* 面板 */}
        {this.panelOpen && (
          <AskQuestionPanel
            targetType={ctx.targetType}
            targetId={ctx.targetId}
            onclose={() => this.closePanel()}
          />
        )}
      </div>
    );
  }
}
